import React, { useState, useEffect } from "react";
import {
    ThemeProvider,
    Typography,
    Grid,
    Box,
    Card,
    CardHeader,
    CardContent,
    Breadcrumbs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    TextField,
    Button,
    Paper,
    Link,
    Snackbar,
    Alert
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { createTheme } from "@mui/material/styles";
import { darken } from '@mui/system';
import { getTechnicians } from "../API/technicianAPI";
import { getJobOrders } from "../API/jobOrderAPI";
import { Bar, Pie } from 'react-chartjs-2';
import 'chart.js/auto';
import { getLeads } from "../API/leadAPI";
import { getSalesRep } from "../API/salesRepAPI";
import BaseWrapper from "../../components/shared/BaseWrapper";


const theme = createTheme({
    palette: {
        primary: {
            main: "#1e5d45", // Custom primary color
        },
    },
});

const StyledHr = styled("hr")(({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    // margin: theme.spacing(4, 0),
    opacity: 0.8,
}));

const formatTime = (time) => {
    if (!time) return "N/A";  // Handle cases where time might be missing

    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12; // Convert 0 to 12 for midnight

    return `${formattedHour}:${minutes} ${period}`;
};


const FunnelSection = styled(Box)(({ bgColor, width }) => ({
    textAlign: 'center',
    padding: '30px 0',
    margin: '0',
    color: 'white',
    fontWeight: 'bold',
    backgroundColor: bgColor,
    borderRadius: '50% / 20%', // Creates a cylindrical look
    width: width,
    height: '50px', // Fixed height for cylinder sections
    transition: 'background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease',
    boxShadow: '0 5px 10px rgba(0, 0, 0, 0.1)',
    position: 'relative', // For stacking tapering effect
    '&:hover': {
        backgroundColor: darken(bgColor, 0.2), // Darkens the background on hover
        transform: 'scale(1.05)',
        textDecoration: 'none', // Removes underline formatting
    },
    '&:after': {
        content: '""',
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        height: '10px', // Tapering effect height
        zIndex: -1,
    },
}));



// Main Funnel Container with Styled(Box)
const FunnelContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '50%',
    margin: 'auto',
    padding: '20px 0',
}));


function ViewLeadStatusReport() {

    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false); // State to manage loading
    const [searchFilter, setSearchFilter] = useState({
        startDate: (() => {
            const now = new Date();
            const year = now.getUTCFullYear(); // Use UTC year
            const month = now.getUTCMonth(); // Use UTC month

            // Calculate the last day of the current month using UTC
            const lastDayOfMonth = new Date(Date.UTC(year, month, 1));

            // Format the date as YYYY-MM-DD
            const formattedFirstDay = lastDayOfMonth.toISOString().split("T")[0];

            return formattedFirstDay;
        })(),
        endDate: (() => {
            const now = new Date();
            const year = now.getUTCFullYear(); // Use UTC year
            const month = now.getUTCMonth(); // Use UTC month

            // Calculate the last day of the current month using UTC
            const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0));

            // Format the date as YYYY-MM-DD
            const formattedLastDay = lastDayOfMonth.toISOString().split("T")[0];

            return formattedLastDay;
        })()



    });

    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [leadStatuses, setLeadStatuses] = useState([])
    const [leadOpen, setLeadOpen] = useState([])
    const [leadContacted, setLeadContacted] = useState([])
    const [leadOcular, setLeadOcular] = useState([])
    const [leadClosed, setLeadClosed] = useState([])
    const [leadFunnel, setLeadFunnel] = useState({})


    const handleSubmitButtonClick = async () => {
        try {
            setIsLoading(true);

            // Fetch leads using the provided date range
            const leads = await getLeads(
                null,
                null,
                null,
                searchFilter.startDate,
                searchFilter.endDate,
            );

            if (!leads || leads.length === 0) {
                setLeadStatuses([]);
                setLeadFunnel({ open: 0, ocular: 0, closed: 0 });
                setLeadOpen([]);
                setLeadContacted([]);
                setLeadOcular([]);
                setLeadClosed([]);
                setIsLoading(false);
                return;
            }

            // Initialize lead funnel counters
            const leadFunnel = {
                open: 0,
                contacted: 0,
                ocular: 0,
                closed: 0,
            };

            // Temporary arrays for split leads
            let openLeads = [];
            let contactedLeads = [];
            let ocularLeads = [];
            let closedLeads = [];


            const leadStatuses = await Promise.all(
                leads.map(async (lead) => {
                    // Fetch sales rep information based on sales_rep_id
                    const salesRepData = await getSalesRep(lead.sales_rep_id);
                    const salesRep = salesRepData
                        ? `${salesRepData.firstname} ${salesRepData.lastname}`
                        : ''; // Keep it blank if no sales rep is found

                    // Categorize leads based on lead.status
                    let leadCategory;
                    switch (lead.status) {
                        case 'open':
                            leadCategory = 'open';
                            leadFunnel.open += 1;
                            openLeads.push({
                                id: lead.id,
                                status: lead.status,
                                client: lead.client,
                                created_date: lead.created_date,
                                salesRep: salesRep,
                            });
                            break;
                        case 'contacted':
                            leadCategory = 'contacted';
                            leadFunnel.contacted += 1;
                            contactedLeads.push({
                                id: lead.id,
                                status: lead.status,
                                client: lead.client,
                                created_date: lead.created_date,
                                salesRep: salesRep,
                            });
                            break;
                        case 'ocular':
                            leadCategory = 'ocular';
                            leadFunnel.ocular += 1;
                            ocularLeads.push({
                                id: lead.id,
                                status: lead.status,
                                client: lead.client,
                                created_date: lead.created_date,
                                salesRep: salesRep,
                            });
                            break;
                        case 'closed':
                            leadCategory = 'closed';
                            leadFunnel.closed += 1;
                            closedLeads.push({
                                id: lead.id,
                                status: lead.status,
                                client: lead.client,
                                created_date: lead.created_date,
                                salesRep: salesRep,
                            });
                            break;
                        default:
                            console.warn(`Unknown lead status: ${lead.status}`);
                            break;
                    }

                    // Return the transformed lead object
                    return {
                        id: lead.id,
                        status: lead.status,
                        client: lead.client,
                        salesRep: salesRep,
                        category: leadCategory,
                    };
                })
            );




            // At this point, openLeads, contactedLeads, ocularLeads, and closedLeads are populated
            // leadStatuses contains all the transformed lead objects.


            // Transform leads and fetch job orders for each client_id
            // const leadStatuses = await Promise.all(
            //     leads.map(async (lead) => {
            //         // Fetch job orders for the client's ID
            //         const jobOrders = await getJobOrders([], [], [], null, null, lead.client_id);

            //         // Fetch sales rep information based on sales_rep_id
            //         const salesRepData = await getSalesRep(lead.sales_rep_id);
            //         const salesRep = salesRepData
            //             ? `${salesRepData.firstname} ${salesRepData.lastname}`
            //             : '';  // Keep it blank if no sales rep is found

            //         // Sort job orders by timestamp_create and keep the earliest two
            //         const sortedJobOrders = jobOrders
            //             ? jobOrders.sort((a, b) => new Date(a.timestamp_create) - new Date(b.timestamp_create))
            //             : [];

            //         const limitedJobOrders = sortedJobOrders.slice(0, 2);

            //         let leadCategory;

            //         // Update lead funnel counts based on job orders
            //         if (limitedJobOrders.length === 0) {
            //             leadFunnel.open += 1;
            //             leadCategory = 'open';
            //             openLeads.push({
            //                 id: lead.id,
            //                 status: lead.status,
            //                 client: lead.client,
            //                 created_date: lead.created_date,
            //                 jobOrders: [],
            //                 salesRep: salesRep,  // Add sales rep info (blank if not found)
            //             });
            //         } else {
            //             const jobType = limitedJobOrders[0]?.job_type;

            //             if (jobType === 'ocular') {
            //                 leadFunnel.ocular += 1;
            //                 leadCategory = 'ocular';
            //                 ocularLeads.push({
            //                     id: lead.id,
            //                     status: lead.status,
            //                     client: lead.client,
            //                     created_date: lead.created_date,
            //                     jobOrders: limitedJobOrders,
            //                     salesRep: salesRep,  // Add sales rep info (blank if not found)
            //                 });
            //             }

            //             if (limitedJobOrders.some((job) => job.job_type === 'treatment')) {
            //                 leadFunnel.closed += 1;
            //                 leadCategory = 'closed';
            //                 closedLeads.push({
            //                     id: lead.id,
            //                     status: lead.status,
            //                     client: lead.client,
            //                     created_date: lead.created_date,
            //                     jobOrders: limitedJobOrders,
            //                     salesRep: salesRep,  // Add sales rep info (blank if not found)
            //                 });
            //             }
            //         }

            //         // Return the transformed lead object
            //         return {
            //             id: lead.id,
            //             status: lead.status,
            //             client: lead.client,
            //             jobOrders: limitedJobOrders,
            //             salesRep: salesRep,
            //             category: leadCategory,
            //         };
            //     })
            // );

            // Sort each lead list by created_date (earliest to latest)
            openLeads.sort((a, b) => new Date(a.created_date) - new Date(b.created_date));
            contactedLeads.sort((a, b) => new Date(a.created_date) - new Date(b.created_date));
            ocularLeads.sort((a, b) => new Date(a.created_date) - new Date(b.created_date));
            closedLeads.sort((a, b) => new Date(a.created_date) - new Date(b.created_date));

            // Update state with the split leads and lead funnel
            setLeadStatuses(leadStatuses);
            setLeadFunnel(leadFunnel);
            setLeadOpen(openLeads);
            setLeadContacted(contactedLeads);
            setLeadOcular(ocularLeads);
            setLeadClosed(closedLeads);

            setError(null);  // Clear any previous errors
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };



    // Example usage of FunnelSection and FunnelContainer
    const funnelSteps = [
        { label: 'Open', value: leadFunnel.open, color: '#B71C1C', width: '100%' }, // Darker red
        { label: 'Contacted', value: leadFunnel.contacted, color: '#E65100', width: '80%' }, // Darker orange
        { label: 'Ocular', value: leadFunnel.ocular, color: '#F9A825', width: '60%' }, // Darker yellow
        { label: 'Closed', value: leadFunnel.closed, color: '#2E7D32', width: '40%' }, // Darker green
    ];



    return (


        <BaseWrapper
            titleIcon={<AnalyticsIcon />}
            title="Sales"
            subTitle="View Lead Status Report"
            subTitleRoute={"/view-lead-status-sales-report"}
            // createButtonLabel="Create Sales Representative"
            // onClickCreateButton={() => navigate("/create-salesrep")}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            isLoading={isLoading}
        >
            <Grid item xs={12} sm={12}>
                <Grid container spacing={3} sx={{ mb: 2 }} alignItems="center">
                    <Grid item xs={12} sm={3} md={3}>
                        <TextField
                            label="Start Date"
                            type="date"
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={searchFilter.startDate}
                            onChange={(e) =>
                                setSearchFilter((prev) => ({
                                    ...prev,
                                    startDate: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={3} md={3}>
                        <TextField
                            label="End Date"
                            type="date"
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={searchFilter.endDate}
                            onChange={(e) =>
                                setSearchFilter((prev) => ({
                                    ...prev,
                                    endDate: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={2}
                        container
                    // justifyContent="flex-end"
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmitButtonClick}
                            disabled={isLoading}
                        >
                            {isLoading ? "Submitting..." : "Submit"}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>



            {(leadFunnel.open || leadFunnel.contacted || leadFunnel.ocular || leadFunnel.closed) > 0 && (
                <FunnelContainer>
                    {funnelSteps.map((step, index) => (
                        <FunnelSection
                            key={index}
                            bgColor={step.color}
                            width={step.width}
                            baseWidth={step.baseWidth}
                        >
                            <Typography variant="h6">{step.label}</Typography>
                            <Typography variant="h3">{step.value}</Typography>
                        </FunnelSection>
                    ))}
                </FunnelContainer>
            )}

            {leadStatuses.length > 0 && (
                <Grid container spacing={3}>
                    <Grid item xs={3} md={3}>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}
                        >
                            Open
                        </Typography>
                        {leadOpen.length > 0 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Created Date
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Client
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Sales Rep
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {leadOpen.map((lead, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.created_date ? (
                                                        <a
                                                            href={`/view-lead/${lead.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: "#1976d2",
                                                                textDecoration: "none",
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {lead.created_date}
                                                        </a>
                                                    ) : (
                                                        ""
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.client?.name || ""}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.salesRep || ""}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>

                    <Grid item xs={3} md={3}>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}
                        >
                            Contacted
                        </Typography>
                        {leadContacted.length > 0 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Created Date
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Client
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Sales Rep
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {leadContacted.map((lead, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.created_date ? (
                                                        <a
                                                            href={`/view-lead/${lead.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: "#1976d2",
                                                                textDecoration: "none",
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {lead.created_date}
                                                        </a>
                                                    ) : (
                                                        ""
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.client?.name || ""}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.salesRep || ""}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>

                    <Grid item xs={3} md={3}>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}
                        >
                            Ocular
                        </Typography>
                        {leadOcular.length > 0 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Created Date
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Client
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Sales Rep
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {leadOcular.map((lead, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.created_date ? (
                                                        <a
                                                            href={`/view-lead/${lead.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: "#1976d2",
                                                                textDecoration: "none",
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {lead.created_date}
                                                        </a>
                                                    ) : (
                                                        ""
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.client?.name || ""}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.salesRep || ""}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>

                    <Grid item xs={3} md={3}>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}
                        >
                            Closed
                        </Typography>
                        {leadClosed.length > 0 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Created Date
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Client
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Sales Rep
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {leadClosed.map((lead, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.created_date ? (
                                                        <a
                                                            href={`/view-lead/${lead.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: "#1976d2",
                                                                textDecoration: "none",
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {lead.created_date}
                                                        </a>
                                                    ) : (
                                                        ""
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.client?.name || ""}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {lead.salesRep || ""}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>
                </Grid>
            )}

        </BaseWrapper>
        // <ThemeProvider theme={theme}>
        //     <Snackbar
        //         open={isLoading}
        //         anchorOrigin={{ vertical: "top", horizontal: "center" }}
        //     >
        //         <Alert
        //             severity="info"
        //             icon={<CircularProgress size={18} />}
        //             sx={{
        //                 display: "flex",
        //                 justifyContent: "center",
        //                 alignItems: "center",
        //                 width: "auto",
        //                 maxWidth: "100%",
        //                 mx: "auto",
        //             }}
        //         >
        //             Loading, please wait...
        //         </Alert>
        //     </Snackbar>
        //     <Box sx={{ padding: "40px 60px" }}>
        //         <Card
        //             variant="outlined"
        //             style={{
        //                 marginTop: "40px",
        //                 marginBottom: "40px",
        //                 borderRadius: 0,
        //             }}
        //         >
        //             <CardHeader
        //                 title={
        //                     <Box
        //                         display="flex"
        //                         alignItems="center"
        //                         justifyContent="space-between"
        //                     >
        //                         <Box display="flex" alignItems="center">
        //                             <AnalyticsIcon
        //                                 sx={{
        //                                     fontSize: "40px",
        //                                     marginRight: "8px",
        //                                 }}
        //                             />
        //                             <Typography variant="h6">Sales</Typography>
        //                         </Box>
        //                         <Breadcrumbs
        //                             aria-label="breadcrumb"
        //                             separator={<NavigateNextIcon fontSize="small" />}
        //                             sx={{ color: "white" }}
        //                         >
        //                             <Typography sx={{ color: "#ffffff99" }}>
        //                                 Sales
        //                             </Typography>
        //                             <Typography sx={{ color: "#ffffff", textDecoration: "none" }}>
        //                                 View Lead Status Report
        //                             </Typography>
        //                         </Breadcrumbs>
        //                     </Box>
        //                 }
        //                 sx={{
        //                     backgroundColor: "#1e5d45",
        //                     color: "white",
        //                     padding: "16px",
        //                 }}
        //             />
        //             <CardContent sx={{ padding: "20px" }}>
        //                 <Card variant="outlined" sx={{ padding: "20px" }}>
        //                     <CardHeader
        //                         title={
        //                             <Typography variant="h5" sx={{ fontWeight: "bold" }}>
        //                                 Lead Status Report
        //                             </Typography>
        //                         }
        //                         sx={{ marginBottom: 2 }}
        //                     />
        //                     <Grid item xs={12} sm={12}>
        //                         <Grid container spacing={3} sx={{ mb: 2 }} alignItems="center">
        //                             <Grid item xs={12} sm={2} md={2}>
        //                                 <TextField
        //                                     label="Start Date"
        //                                     type="date"
        //                                     variant="outlined"
        //                                     fullWidth
        //                                     InputLabelProps={{
        //                                         shrink: true,
        //                                     }}
        //                                     value={searchFilter.startDate}
        //                                     onChange={(e) =>
        //                                         setSearchFilter((prev) => ({
        //                                             ...prev,
        //                                             startDate: e.target.value,
        //                                         }))
        //                                     }
        //                                 />
        //                             </Grid>
        //                             <Grid item xs={12} sm={2} md={2}>
        //                                 <TextField
        //                                     label="End Date"
        //                                     type="date"
        //                                     variant="outlined"
        //                                     fullWidth
        //                                     InputLabelProps={{
        //                                         shrink: true,
        //                                     }}
        //                                     value={searchFilter.endDate}
        //                                     onChange={(e) =>
        //                                         setSearchFilter((prev) => ({
        //                                             ...prev,
        //                                             endDate: e.target.value,
        //                                         }))
        //                                     }
        //                                 />
        //                             </Grid>
        //                             <Grid
        //                                 item
        //                                 xs={12}
        //                                 sm={12}
        //                                 md={2}
        //                                 container
        //                             // justifyContent="flex-end"
        //                             >
        //                                 <Button
        //                                     variant="contained"
        //                                     color="primary"
        //                                     onClick={handleSubmitButtonClick}
        //                                     disabled={isLoading}
        //                                 >
        //                                     {isLoading ? "Submitting..." : "Submit"}
        //                                 </Button>
        //                             </Grid>
        //                         </Grid>
        //                     </Grid>



        //                     {(leadFunnel.open || leadFunnel.contacted || leadFunnel.ocular || leadFunnel.closed) > 0 && (
        //                         <FunnelContainer>
        //                             {funnelSteps.map((step, index) => (
        //                                 <FunnelSection
        //                                     key={index}
        //                                     bgColor={step.color}
        //                                     width={step.width}
        //                                     baseWidth={step.baseWidth}
        //                                 >
        //                                     <Typography variant="h6">{step.label}</Typography>
        //                                     <Typography variant="h3">{step.value}</Typography>
        //                                 </FunnelSection>
        //                             ))}
        //                         </FunnelContainer>
        //                     )}

        //                     {leadStatuses.length > 0 && (
        //                         <Grid container spacing={3}>
        //                             <Grid item xs={3} md={3}>
        //                                 <Typography
        //                                     variant="subtitle1"
        //                                     sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}
        //                                 >
        //                                     Open
        //                                 </Typography>
        //                                 {leadOpen.length > 0 && (
        //                                     <TableContainer>
        //                                         <Table size="small">
        //                                             <TableHead>
        //                                                 <TableRow>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Created Date
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Client
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Sales Rep
        //                                                     </TableCell>
        //                                                 </TableRow>
        //                                             </TableHead>
        //                                             <TableBody>
        //                                                 {leadOpen.map((lead, index) => (
        //                                                     <TableRow key={index}>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.created_date ? (
        //                                                                 <a
        //                                                                     href={`/view-lead/${lead.id}`}
        //                                                                     target="_blank"
        //                                                                     rel="noopener noreferrer"
        //                                                                     style={{
        //                                                                         color: "#1976d2",
        //                                                                         textDecoration: "none",
        //                                                                         fontWeight: "bold",
        //                                                                     }}
        //                                                                 >
        //                                                                     {lead.created_date}
        //                                                                 </a>
        //                                                             ) : (
        //                                                                 ""
        //                                                             )}
        //                                                         </TableCell>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.client?.name || ""}
        //                                                         </TableCell>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.salesRep || ""}
        //                                                         </TableCell>
        //                                                     </TableRow>
        //                                                 ))}
        //                                             </TableBody>
        //                                         </Table>
        //                                     </TableContainer>
        //                                 )}
        //                             </Grid>

        //                             <Grid item xs={3} md={3}>
        //                                 <Typography
        //                                     variant="subtitle1"
        //                                     sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}
        //                                 >
        //                                     Contacted
        //                                 </Typography>
        //                                 {leadContacted.length > 0 && (
        //                                     <TableContainer>
        //                                         <Table size="small">
        //                                             <TableHead>
        //                                                 <TableRow>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Created Date
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Client
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Sales Rep
        //                                                     </TableCell>
        //                                                 </TableRow>
        //                                             </TableHead>
        //                                             <TableBody>
        //                                                 {leadContacted.map((lead, index) => (
        //                                                     <TableRow key={index}>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.created_date ? (
        //                                                                 <a
        //                                                                     href={`/view-lead/${lead.id}`}
        //                                                                     target="_blank"
        //                                                                     rel="noopener noreferrer"
        //                                                                     style={{
        //                                                                         color: "#1976d2",
        //                                                                         textDecoration: "none",
        //                                                                         fontWeight: "bold",
        //                                                                     }}
        //                                                                 >
        //                                                                     {lead.created_date}
        //                                                                 </a>
        //                                                             ) : (
        //                                                                 ""
        //                                                             )}
        //                                                         </TableCell>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.client?.name || ""}
        //                                                         </TableCell>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.salesRep || ""}
        //                                                         </TableCell>
        //                                                     </TableRow>
        //                                                 ))}
        //                                             </TableBody>
        //                                         </Table>
        //                                     </TableContainer>
        //                                 )}
        //                             </Grid>

        //                             <Grid item xs={3} md={3}>
        //                                 <Typography
        //                                     variant="subtitle1"
        //                                     sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}
        //                                 >
        //                                     Ocular
        //                                 </Typography>
        //                                 {leadOcular.length > 0 && (
        //                                     <TableContainer>
        //                                         <Table size="small">
        //                                             <TableHead>
        //                                                 <TableRow>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Created Date
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Client
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Sales Rep
        //                                                     </TableCell>
        //                                                 </TableRow>
        //                                             </TableHead>
        //                                             <TableBody>
        //                                                 {leadOcular.map((lead, index) => (
        //                                                     <TableRow key={index}>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.created_date ? (
        //                                                                 <a
        //                                                                     href={`/view-lead/${lead.id}`}
        //                                                                     target="_blank"
        //                                                                     rel="noopener noreferrer"
        //                                                                     style={{
        //                                                                         color: "#1976d2",
        //                                                                         textDecoration: "none",
        //                                                                         fontWeight: "bold",
        //                                                                     }}
        //                                                                 >
        //                                                                     {lead.created_date}
        //                                                                 </a>
        //                                                             ) : (
        //                                                                 ""
        //                                                             )}
        //                                                         </TableCell>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.client?.name || ""}
        //                                                         </TableCell>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.salesRep || ""}
        //                                                         </TableCell>
        //                                                     </TableRow>
        //                                                 ))}
        //                                             </TableBody>
        //                                         </Table>
        //                                     </TableContainer>
        //                                 )}
        //                             </Grid>

        //                             <Grid item xs={3} md={3}>
        //                                 <Typography
        //                                     variant="subtitle1"
        //                                     sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}
        //                                 >
        //                                     Closed
        //                                 </Typography>
        //                                 {leadClosed.length > 0 && (
        //                                     <TableContainer>
        //                                         <Table size="small">
        //                                             <TableHead>
        //                                                 <TableRow>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Created Date
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Client
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.8rem",
        //                                                             fontWeight: "bold",
        //                                                             padding: "6px",
        //                                                             backgroundColor: "#f7f7f7",
        //                                                         }}
        //                                                     >
        //                                                         Sales Rep
        //                                                     </TableCell>
        //                                                 </TableRow>
        //                                             </TableHead>
        //                                             <TableBody>
        //                                                 {leadClosed.map((lead, index) => (
        //                                                     <TableRow key={index}>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.created_date ? (
        //                                                                 <a
        //                                                                     href={`/view-lead/${lead.id}`}
        //                                                                     target="_blank"
        //                                                                     rel="noopener noreferrer"
        //                                                                     style={{
        //                                                                         color: "#1976d2",
        //                                                                         textDecoration: "none",
        //                                                                         fontWeight: "bold",
        //                                                                     }}
        //                                                                 >
        //                                                                     {lead.created_date}
        //                                                                 </a>
        //                                                             ) : (
        //                                                                 ""
        //                                                             )}
        //                                                         </TableCell>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.client?.name || ""}
        //                                                         </TableCell>
        //                                                         <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                             {lead.salesRep || ""}
        //                                                         </TableCell>
        //                                                     </TableRow>
        //                                                 ))}
        //                                             </TableBody>
        //                                         </Table>
        //                                     </TableContainer>
        //                                 )}
        //                             </Grid>
        //                         </Grid>
        //                     )}





        //                 </Card>
        //             </CardContent>
        //         </Card>
        //     </Box>
        // </ThemeProvider >
    )

}

export default ViewLeadStatusReport;